/**
 * Contacts — `contacts.html` ported.
 *
 * Unlike the page this replaces, `contacts` was already a fully-built backend
 * module (list/create/get/patch/delete/restore/duplicates/relationships/
 * account-links) that the frontend had never caught up to — same story as
 * `accounts`. The one real gap was `GET /contacts/anniversaries`, added
 * alongside this screen; see `salescrmapi/docs/modules/contacts.model.md`.
 *
 * The prototype's header also offers Import (§14, not built), a global
 * Duplicate Suggestions inbox (no batch-scan endpoint exists — only the
 * per-record check `ContactFormModal` already uses live), and Relationship
 * Map / Anniversaries, both real here.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import {
  Badge, Avatar, SearchInput, ChipSelect, MockBadge, ErrorState,
} from '../../components/common/index.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { isMock } from '../../api/mockFallback.js';
import { relativeTime, number } from '../../utils/formatters.js';
import {
  useContacts, useContactOptions, useCreateContact, useUpdateContact,
  useDeleteContact, useContactAnniversaries,
} from './useContacts.js';
import {
  toRow, ROLE_TONE, channelIcon, DECISION_MAKER_ROLES,
} from './contactShape.js';
import { ContactFormModal, DeleteContactModal, NotBuiltModal } from './ContactModals.jsx';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';
import { AnniversariesModal } from './AnniversariesModal.jsx';
import { RelationshipMapModal } from './RelationshipMapModal.jsx';

const INFLUENCE_OPTIONS = ['Low', 'Medium', 'High'];
const CHANNEL_OPTIONS = ['Email', 'Phone', 'WhatsApp', 'Teams', 'LinkedIn', 'In person'];

/** `email` used to live here — §8 is real now, so the row icon opens `EmailComposerModal`. */
const NOT_BUILT = {
  import: {
    title: 'Bulk import is not available',
    why: '§14 (bulk/import/export) specifies a mapping-and-preview flow with its own job table. None of that is built, so a real import here would silently do nothing.',
    endpoint: 'POST /imports',
    section: 'docs/API.md §14, not implemented',
  },
  duplicateInbox: {
    title: 'An org-wide duplicate inbox is not available',
    why: 'The real duplicate check exists — GET /contacts/{id}/duplicates, matching on email, phone, and account+surname — but it runs per record. A standing inbox of every duplicate across the org needs a batch scan the backend doesn’t build, so this can’t honestly show a list. Open New Contact and start typing a name or email instead: the same check runs live.',
    endpoint: 'GET /contacts/{id}/duplicates',
    section: 'docs/API.md §5',
  },
};

export default function ContactsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [decisionMakersOnly, setDecisionMakersOnly] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [influence, setInfluence] = useState('');
  const [channel, setChannel] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [notBuilt, setNotBuilt] = useState(null);
  const [emailContact, setEmailContact] = useState(null);
  const [annivOpen, setAnnivOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [mapAccountId, setMapAccountId] = useState('');

  const [sort, setSort] = useState('full_name');

  const debounced = useDebounce(search);
  const pages = useCursorPages(25);
  const options = useContactOptions();

  const params = useMemo(() => ({
    ...pages.params,
    sort,
    q: debounced || undefined,
    account_id: accountId || undefined,
    influence: influence || undefined,
    preferred_channel: channel || undefined,
    buying_role: decisionMakersOnly ? DECISION_MAKER_ROLES.join(',') : undefined,
  }), [pages.params, sort, debounced, accountId, influence, channel, decisionMakersOnly]);

  const { data, isLoading, error, refetch } = useContacts(params);
  const rows = useMemo(() => (data?.data ?? []).map(toRow), [data]);

  const { data: soonData } = useContactAnniversaries(7);
  const dueSoonCount = (soonData?.data ?? []).length;

  const createMutation = useCreateContact();
  const updateMutation = useUpdateContact();
  const deleteMutation = useDeleteContact();

  const onFilter = (setter) => (value) => { setter(value); pages.reset(); };

  const openRelationshipMap = (contact) => {
    setMapAccountId(contact.account?.id ?? '');
    setMapOpen(true);
  };

  const columns = [
    {
      key: 'name',
      header: 'Contact',
      sortKey: 'full_name',
      render: (c) => (
        <div className="d-flex align-items-center gap-2">
          <Avatar name={c.fullName} size={32} />
          <div className="min-w-0">
            <a
              href={`#${c.id}`}
              className="fw-600 d-block text-truncate"
              onClick={(e) => { e.preventDefault(); setEditing(c); setFormOpen(true); }}
            >
              {c.fullName}
            </a>
            <div className="t-xs text-muted text-truncate">{c.jobTitle ?? '—'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      width: 170,
      render: (c) => (c.account
        ? <a href={`/accounts/${c.account.id}`} onClick={(e) => { e.preventDefault(); navigate(`/accounts/${c.account.id}`); }}>{c.account.name}</a>
        : <span className="text-muted">—</span>),
    },
    {
      key: 'role', header: 'Role', width: 140,
      render: (c) => (c.buyingRole ? <Badge tone={ROLE_TONE[c.buyingRole] ?? 'neutral'}>{c.buyingRole}</Badge> : <span className="text-muted">—</span>),
    },
    {
      key: 'influence', header: 'Influence', width: 100, sortKey: 'influence',
      render: (c) => (c.influence
        ? <span className={`t-xs fw-650 ${c.influence === 'High' ? 'text-danger' : c.influence === 'Medium' ? 'text-warning' : 'ink-4'}`}>{c.influence}</span>
        : <span className="text-muted">—</span>),
    },
    {
      key: 'channel', header: 'Preferred channel', width: 150,
      render: (c) => (c.preferredChannel
        ? <span className="t-xs ink-2"><i className={`bi bi-${channelIcon(c.preferredChannel)} me-1`} aria-hidden="true" />{c.preferredChannel}</span>
        : <span className="text-muted">—</span>),
    },
    {
      key: 'lastTouch', header: 'Last touch', width: 120, sortKey: 'last_activity_at',
      render: (c) => <span className="t-xs text-muted">{c.lastActivityAt ? relativeTime(c.lastActivityAt) : 'Never'}</span>,
    },
    {
      key: 'actions', header: '', width: 130, align: 'end',
      render: (c) => (
        <div className="row-actions d-inline-flex gap-1">
          {c.phone
            ? <a className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Call" href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()}><i className="bi bi-telephone" aria-hidden="true" /></a>
            : <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Call" disabled><i className="bi bi-telephone" aria-hidden="true" /></button>}
          <button
            type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Email"
            onClick={(e) => { e.stopPropagation(); setEmailContact(c); }}
          >
            <i className="bi bi-envelope" aria-hidden="true" />
          </button>
          <button
            type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Relationship map"
            onClick={(e) => { e.stopPropagation(); openRelationshipMap(c); }}
          >
            <i className="bi bi-diagram-3" aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb"><a href="#sell">Sell</a><i className="bi bi-chevron-right" style={{ fontSize: 9 }} aria-hidden="true" /><span>Contacts</span></div>
          <h1 className="page-title">Contacts</h1>
          <p className="page-sub">
            {number(data?.page?.total ?? rows.length)} people
            {isMock(data) && <MockBadge section="contacts" />}
          </p>
        </div>
        <div className="ms-auto d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setNotBuilt(NOT_BUILT.import)}>
            <i className="bi bi-upload me-1" aria-hidden="true" />Import
          </button>
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setAnnivOpen(true)}>
            <i className="bi bi-balloon-fill me-1" aria-hidden="true" />Anniversaries
            {dueSoonCount > 0 && <span className="pill pill-rose ms-1">{dueSoonCount}</span>}
          </button>
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => { setMapAccountId(''); setMapOpen(true); }}>
            <i className="bi bi-diagram-3-fill me-1" aria-hidden="true" />Relationship Map
          </button>
          <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setNotBuilt(NOT_BUILT.duplicateInbox)}>
            <i className="bi bi-people-fill me-1" aria-hidden="true" />Duplicate Suggestions
          </button>
          {can('contacts', 'create') && (
            <button type="button" className="btn btn-add btn-sm-x" onClick={() => { setEditing(null); setFormOpen(true); }}>
              <i className="bi bi-person-plus me-1" aria-hidden="true" />New contact
            </button>
          )}
        </div>
      </div>

      <div className="card-x fade-up">
        <div className="d-flex flex-wrap align-items-center gap-2 p-3 border-bottom">
          <div style={{ maxWidth: 240 }}>
            <SearchInput value={search} onChange={onFilter(setSearch)} placeholder="Search contacts…" />
          </div>
          <button
            type="button" className={`chip ${decisionMakersOnly ? 'active' : ''}`}
            onClick={() => onFilter(setDecisionMakersOnly)(!decisionMakersOnly)}
          >
            Decision makers {decisionMakersOnly && <i className="bi bi-x-lg x" aria-hidden="true" />}
          </button>
          <ChipSelect label="Account" allLabel="All accounts" value={accountId} onChange={onFilter(setAccountId)} options={options.accounts} />
          <ChipSelect label="Influence" allLabel="All influence levels" value={influence} onChange={onFilter(setInfluence)} options={INFLUENCE_OPTIONS} />
          <ChipSelect label="Preferred channel" allLabel="All channels" value={channel} onChange={onFilter(setChannel)} options={CHANNEL_OPTIONS} />
        </div>

        {error ? (
          <div className="p-4"><ErrorState error={error} onRetry={refetch} /></div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            isLoading={isLoading}
            page={data?.page}
            onNext={pages.goNext}
            onPrevious={pages.goPrevious}
            canGoPrevious={pages.canGoPrevious}
            sort={sort}
            onSort={(s) => { setSort(s); pages.reset(); }}
            emptyTitle="No contacts match these filters"
          />
        )}
      </div>

      <ContactFormModal
        open={formOpen}
        contact={editing}
        options={options}
        busy={createMutation.isPending || updateMutation.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={async (payload) => {
          if (editing) {
            await updateMutation.mutateAsync({ id: editing.id, patch: payload, rowVersion: editing.rowVersion });
          } else {
            await createMutation.mutateAsync(payload);
          }
          setFormOpen(false);
        }}
        onDelete={() => { setFormOpen(false); setDeleting(editing); }}
      />

      <DeleteContactModal
        open={Boolean(deleting)}
        contact={deleting}
        busy={deleteMutation.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async (reason) => {
          await deleteMutation.mutateAsync({ id: deleting.id, reason, rowVersion: deleting.rowVersion });
          setDeleting(null);
        }}
      />

      <AnniversariesModal open={annivOpen} onClose={() => setAnnivOpen(false)} />
      <RelationshipMapModal open={mapOpen} onClose={() => setMapOpen(false)} initialAccountId={mapAccountId} />
      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />

      <EmailComposerModal
        open={Boolean(emailContact)}
        onClose={() => setEmailContact(null)}
        anchor={{ contact_id: emailContact?.id }}
        defaultEmail={emailContact?.email}
      />
    </>
  );
}
